import {
  collection,
  doc,
  getDocs,
  getDoc,
  addDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  onSnapshot,
  serverTimestamp
} from 'firebase/firestore';
import { db } from './config';

// Mock data for development when Firebase is not configured
const mockData = {
  equipment: [
    {
      id: 'mock-1',
      name: 'Mock Audio Interface',
      brand: 'Mock Brand',
      category: 'Audio Interface',
      price: 299,
      rating: 4.5,
      reviewCount: 10,
      imageUrl: 'https://via.placeholder.com/300x200',
      createdAt: new Date(),
      updatedAt: new Date()
    }
  ],
  articles: [
    {
      id: 'mock-1',
      title: 'Mock Article Title',
      slug: 'mock-article',
      content: 'This is mock content for development.',
      excerpt: 'Mock excerpt',
      authorName: 'Mock Author',
      category: 'general',
      status: 'published',
      viewCount: 100,
      likeCount: 5,
      createdAt: new Date(),
      updatedAt: new Date()
    }
  ],
  ratings: [],
  users: []
};

// Generic CRUD operations
export const createDocument = async (collectionName, data) => {
  try {
    if (!db) {
      console.warn('Firestore not configured. Using mock data.');
      const mockId = `mock-${Date.now()}`;
      const mockDoc = { id: mockId, ...data, createdAt: new Date(), updatedAt: new Date() };
      if (!mockData[collectionName]) mockData[collectionName] = [];
      mockData[collectionName].push(mockDoc);
      return { success: true, id: mockId };
    }
    
    const docRef = await addDoc(collection(db, collectionName), {
      ...data,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
    return { success: true, id: docRef.id };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const getDocument = async (collectionName, docId) => {
  try {
    if (!db) {
      console.warn('⚠️ Firestore not configured. Using mock data.');
      const mockDoc = mockData[collectionName]?.find(doc => doc.id === docId);
      if (mockDoc) {
        return { success: true, data: mockDoc };
      } else {
        return { success: false, error: 'Document not found' };
      }
    }
    
    console.log('🔍 Firestore: Getting document:', collectionName, docId);
    const docRef = doc(db, collectionName, docId);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      console.log('✅ Firestore: Document found:', docSnap.data());
      return { success: true, data: { id: docSnap.id, ...docSnap.data() } };
    } else {
      console.log('❌ Firestore: Document not found');
      return { success: false, error: 'Document not found' };
    }
  } catch (error) {
    console.error('❌ Firestore: Error getting document:', error);
    
    // Handle specific Firebase errors
    if (error.code === 'permission-denied') {
      console.warn('⚠️ Firestore: Permission denied - user may not be authenticated or document may not exist');
      return { success: false, error: 'Permission denied' };
    } else if (error.code === 'unavailable') {
      console.warn('⚠️ Firestore: Service unavailable - using fallback');
      return { success: false, error: 'Service unavailable' };
    }
    
    return { success: false, error: error.message };
  }
};

export const updateDocument = async (collectionName, docId, data) => {
  try {
    const docRef = doc(db, collectionName, docId);
    await updateDoc(docRef, {
      ...data,
      updatedAt: serverTimestamp()
    });
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const deleteDocument = async (collectionName, docId) => {
  try {
    const docRef = doc(db, collectionName, docId);
    await deleteDoc(docRef);
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const getCollection = async (collectionName, filters = [], orderByField = null, orderDirection = 'asc', limitCount = null) => {
  try {
    if (!db) {
      console.warn('Firestore not configured. Using mock data.');
      let documents = mockData[collectionName] || [];
      
      // Apply filters
      filters.forEach(filter => {
        documents = documents.filter(doc => {
          switch (filter.operator) {
            case '==':
              return doc[filter.field] === filter.value;
            case '!=':
              return doc[filter.field] !== filter.value;
            case '>':
              return doc[filter.field] > filter.value;
            case '<':
              return doc[filter.field] < filter.value;
            case '>=':
              return doc[filter.field] >= filter.value;
            case '<=':
              return doc[filter.field] <= filter.value;
            default:
              return true;
          }
        });
      });
      
      // Apply ordering
      if (orderByField) {
        documents.sort((a, b) => {
          const aVal = a[orderByField];
          const bVal = b[orderByField];
          const comparison = aVal < bVal ? -1 : aVal > bVal ? 1 : 0;
          return orderDirection === 'desc' ? -comparison : comparison;
        });
      }
      
      // Apply limit
      if (limitCount) {
        documents = documents.slice(0, limitCount);
      }
      
      return { success: true, data: documents };
    }
    
    let q = collection(db, collectionName);
    
    // Apply filters
    filters.forEach(filter => {
      q = query(q, where(filter.field, filter.operator, filter.value));
    });
    
    // Apply ordering
    if (orderByField) {
      q = query(q, orderBy(orderByField, orderDirection));
    }
    
    // Apply limit
    if (limitCount) {
      q = query(q, limit(limitCount));
    }
    
    const querySnapshot = await getDocs(q);
    const documents = [];
    
    querySnapshot.forEach((doc) => {
      documents.push({ id: doc.id, ...doc.data() });
    });
    
    return { success: true, data: documents };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// Real-time listeners
export const subscribeToCollection = (collectionName, callback, filters = [], orderByField = null, orderDirection = 'asc') => {
  let q = collection(db, collectionName);
  
  // Apply filters
  filters.forEach(filter => {
    q = query(q, where(filter.field, filter.operator, filter.value));
  });
  
  // Apply ordering
  if (orderByField) {
    q = query(q, orderBy(orderByField, orderDirection));
  }
  
  return onSnapshot(q, (querySnapshot) => {
    const documents = [];
    querySnapshot.forEach((doc) => {
      documents.push({ id: doc.id, ...doc.data() });
    });
    callback(documents);
  });
};

export const subscribeToDocument = (collectionName, docId, callback) => {
  const docRef = doc(db, collectionName, docId);
  
  return onSnapshot(docRef, (docSnap) => {
    if (docSnap.exists()) {
      callback({ id: docSnap.id, ...docSnap.data() });
    } else {
      callback(null);
    }
  });
};

// Equipment-specific operations
export const getEquipment = async (filters = []) => {
  return getCollection('equipment', filters, 'name', 'asc');
};

export const getEquipmentById = async (id) => {
  return getDocument('equipment', id);
};

export const createEquipment = async (equipmentData) => {
  return createDocument('equipment', equipmentData);
};

export const updateEquipment = async (id, equipmentData) => {
  return updateDocument('equipment', id, equipmentData);
};

export const deleteEquipment = async (id) => {
  return deleteDocument('equipment', id);
};

// User-specific operations
export const getUser = async (uid) => {
  return getDocument('users', uid);
};

export const createUser = async (uid, userData) => {
  try {
    if (!db) {
      console.warn('Firestore not configured. Using mock data.');
      const mockDoc = { id: uid, ...userData, uid, createdAt: new Date(), updatedAt: new Date() };
      if (!mockData.users) mockData.users = [];
      mockData.users.push(mockDoc);
      return { success: true, id: uid };
    }
    
    const docRef = doc(db, 'users', uid);
    await setDoc(docRef, {
      ...userData,
      uid,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
    
    return { success: true, id: uid };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const updateUser = async (uid, userData) => {
  return updateDocument('users', uid, userData);
};

// Article-specific operations
export const getArticles = async (filters = []) => {
  return getCollection('articles', filters, 'createdAt', 'desc');
};

export const getArticleById = async (id) => {
  return getDocument('articles', id);
};

export const createArticle = async (articleData) => {
  return createDocument('articles', articleData);
};

export const updateArticle = async (id, articleData) => {
  return updateDocument('articles', id, articleData);
};

export const deleteArticle = async (id) => {
  return deleteDocument('articles', id);
};

// Rating-specific operations
export const getRatings = async (equipmentId) => {
  return getCollection('ratings', [{ field: 'equipmentId', operator: '==', value: equipmentId }], 'createdAt', 'desc');
};

export const createRating = async (ratingData) => {
  return createDocument('ratings', ratingData);
};

export const updateRating = async (id, ratingData) => {
  return updateDocument('ratings', id, ratingData);
};

export const deleteRating = async (id) => {
  return deleteDocument('ratings', id);
};
